import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { Product, CartItem, Cart } from '@/types';
import { STORAGE_KEYS } from '@/utils/constants';

// ─── Actions ──────────────────────────────────────────────────────────────────
type CartAction =
  | { type: 'ADD_ITEM';    product: Product; qty: number; variantId?: string }
  | { type: 'REMOVE_ITEM'; productId: string; variantId?: string }
  | { type: 'UPDATE_QTY';  productId: string; qty: number; variantId?: string }
  | { type: 'CLEAR' }
  | { type: 'HYDRATE';     items: CartItem[] };

// ─── State ────────────────────────────────────────────────────────────────────
interface CartState {
  items: CartItem[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function itemKey(productId: string, variantId?: string) {
  return variantId ? `${productId}__${variantId}` : productId;
}

function calcCart(items: CartItem[]): Cart {
  const subtotal = items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  const originalTotal = items.reduce(
    (sum, i) => sum + (i.product.originalPrice ?? i.product.price) * i.quantity,
    0,
  );
  const discount = Math.max(0, originalTotal - subtotal);
  return {
    items,
    subtotal,
    discount,
    total: subtotal,
    itemCount: items.reduce((sum, i) => sum + i.quantity, 0),
  };
}

// ─── Reducer ──────────────────────────────────────────────────────────────────
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'HYDRATE':
      return { items: action.items };

    case 'ADD_ITEM': {
      const key = itemKey(action.product.id, action.variantId);
      const existing = state.items.find(
        (i) => itemKey(i.product.id, i.variantId) === key,
      );
      if (existing) {
        return {
          items: state.items.map((i) =>
            itemKey(i.product.id, i.variantId) === key
              ? { ...i, quantity: i.quantity + action.qty }
              : i,
          ),
        };
      }
      return {
        items: [
          ...state.items,
          { product: action.product, quantity: action.qty, variantId: action.variantId },
        ],
      };
    }

    case 'REMOVE_ITEM': {
      const key = itemKey(action.productId, action.variantId);
      return {
        items: state.items.filter(
          (i) => itemKey(i.product.id, i.variantId) !== key,
        ),
      };
    }

    case 'UPDATE_QTY': {
      const key = itemKey(action.productId, action.variantId);
      if (action.qty <= 0) {
        return {
          items: state.items.filter(
            (i) => itemKey(i.product.id, i.variantId) !== key,
          ),
        };
      }
      return {
        items: state.items.map((i) =>
          itemKey(i.product.id, i.variantId) === key
            ? { ...i, quantity: action.qty }
            : i,
        ),
      };
    }

    case 'CLEAR':
      return { items: [] };

    default:
      return state;
  }
}

// ─── Context shape ────────────────────────────────────────────────────────────
interface CartContextValue {
  cart: Cart;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (product: Product, qty?: number, variantId?: string) => void;
  removeItem: (productId: string, variantId?: string) => void;
  updateQty: (productId: string, qty: number, variantId?: string) => void;
  clearCart: () => void;
  isInCart: (productId: string, variantId?: string) => boolean;
  getItemQty: (productId: string, variantId?: string) => number;
}

// ─── Context ──────────────────────────────────────────────────────────────────
const CartContext = createContext<CartContextValue | null>(null);

// ─── Persistence helpers ──────────────────────────────────────────────────────
function loadFromStorage(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CART);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartItem[];
    // Validate structure
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (i) => i && i.product && typeof i.product.id === 'string' && typeof i.quantity === 'number',
    );
  } catch {
    return [];
  }
}

function saveToStorage(items: CartItem[]) {
  try {
    localStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(items));
  } catch {
    // Storage quota exceeded — silently fail
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────
interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  const [isOpen, setIsOpen] = useState(false);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const saved = loadFromStorage();
    if (saved.length > 0) {
      dispatch({ type: 'HYDRATE', items: saved });
    }
  }, []);

  // Persist to localStorage on every change
  useEffect(() => {
    saveToStorage(state.items);
  }, [state.items]);

  // Drawer controls
  const openCart   = useCallback(() => setIsOpen(true),  []);
  const closeCart  = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((v) => !v), []);

  // Cart actions
  const addItem = useCallback(
    (product: Product, qty = 1, variantId?: string) => {
      dispatch({ type: 'ADD_ITEM', product, qty, variantId });
    },
    [],
  );

  const removeItem = useCallback(
    (productId: string, variantId?: string) => {
      dispatch({ type: 'REMOVE_ITEM', productId, variantId });
    },
    [],
  );

  const updateQty = useCallback(
    (productId: string, qty: number, variantId?: string) => {
      dispatch({ type: 'UPDATE_QTY', productId, qty, variantId });
    },
    [],
  );

  const clearCart = useCallback(() => dispatch({ type: 'CLEAR' }), []);

  const isInCart = useCallback(
    (productId: string, variantId?: string) =>
      state.items.some(
        (i) => itemKey(i.product.id, i.variantId) === itemKey(productId, variantId),
      ),
    [state.items],
  );

  const getItemQty = useCallback(
    (productId: string, variantId?: string) =>
      state.items.find(
        (i) => itemKey(i.product.id, i.variantId) === itemKey(productId, variantId),
      )?.quantity ?? 0,
    [state.items],
  );

  const cart = useMemo(() => calcCart(state.items), [state.items]);

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      isOpen,
      openCart,
      closeCart,
      toggleCart,
      addItem,
      removeItem,
      updateQty,
      clearCart,
      isInCart,
      getItemQty,
    }),
    [cart, isOpen, openCart, closeCart, toggleCart, addItem, removeItem, updateQty, clearCart, isInCart, getItemQty],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}

// Missing import fix
import { useState } from 'react';
